import React from 'react';
import {
	noop,
	mapValues,
	get,
	find,
	isEmpty,
	cloneDeep,
	defaultTo,
	every,
	isNil,
	forEach,
	isEqual,
	set,
	toUpper,
	indexOf,
	merge,
} from 'lodash';

// import default view
import { FormControl } from './models/FormControl';

// import { formList } from './models/FormList';
import { DATA_VALIDATIONS, MASKS } from 'constants/common';
import { getFieldView } from 'resolvers/form';

/**
 * @type {function}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @param {object} Component - A React component
 * @example
 * import { Form } from 'modules/form';
 *
 * // render
 * <Form propName='propValue'/>
 */
export class Form extends React.Component {
	constructor(props) {
		super(props);
		// bind events and methods
		this.__bind();


		// static data
		this.data = {
			temporaryFieldValues: {},

			// fields
			fields: mapValues(this.props.fields, (field) => ({
				...field,

				// is field required to submit (to display `*` character)
				required: !isEmpty(
					find(get(field, 'config.validations', []), {
						name: DATA_VALIDATIONS.REQUIRED,
					})
				),

				maxLength: get(
					find(
						get(field, 'config.validations', []),
						{ name: DATA_VALIDATIONS.MAX_LENGTH },
						{}
					),
					'maxLength',
					undefined
				),

				// form control
				control: new FormControl({
					name: field.name,
					type: field.type,
					validations: get(field, 'config.validations', []),
					fieldRules: get(field, 'config.fieldRules'),
				}),

				// mask
				mask: get(field, 'config.mask')
					? get(MASKS, field.config.mask)
					: undefined,

				// generic field view
				view: getFieldView(field.type),
			})),
		};

		// component state
		this.state = {
			showErrors: props.showErrors, // display field errors
			isValid: props.values, // is form valid
			errors: mapValues(this.props.fields, () => null), // field errors
			values: mapValues(this.props.fields, (field) => {
				return defaultTo(props.values[field.name], field.config?.emptyValue);
			}), // form values
		};

		const { callingfrom } = this.props;

		if (
			callingfrom &&
			(callingfrom === 'addressAddEdit' || callingfrom === 'contachtInform')
		) {
			this.state.isValid = false;
		}
	}

	// bind events and methods
	__bind() {
		this.handleChange = this.handleChange.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
		this.updateForm = this.updateForm.bind(this);
		this.updateValuesDependentsFields =
			this.updateValuesDependentsFields.bind(this);
	}

	/***************************************/

	/**
	 * @desc Update Dependent fields.
	 */
	updateValuesDependentsFields({ field, value = null } = {}) {
		log.debug('Form.updateValuesDependentsFields()');

		// if no-rules then do nothing
		if (isEmpty(get(field, 'config.fieldRules', []))) {
			return null;
		}

		forEach(field.config.fieldRules, (rule) => {
			switch (rule.name) {
				case 'updateValues': {
					forEach(rule.fields, (_field) => {
						//get the index of value
						const selectedValueIndex = indexOf(rule.checkValue, value);
						if (0 <= selectedValueIndex) {
							// if value found

							this.data.temporaryFieldValues[_field] =
								rule.updateValue[selectedValueIndex]; // since updating multiple value is asynchoronous for one field rule
							this.handleChange({
								name: _field,
								value: rule.updateValue[selectedValueIndex],
								updateDependent: true,
							}); // call onChange on that field with new Rule - Value
						}
					});
					break;
				}
			}
		});

		return null;
	}

	/**
	 * @desc Handle field change.
	 */
	handleChange({ name, value, event, updateDependent = false } = {}) {
		log.debug('Form.handleChange()');

		// clone current form state
		const _state = cloneDeep(this.state);

		if (updateDependent) {
			merge(_state.values, this.data.temporaryFieldValues); // merge the changes when handle change is called asynchronous for value update
		}

		/*-------------------*/

		// get field configuration
		const field = this.data.fields[name];

		// if field is missing, do nothing
		if (!field) {
			return;
		}

		/*-------------------*/

		// update field value (defaults to empty value)
		_state.values[name] = defaultTo(
			field.config.uppercase ? toUpper(value) : value,
			field.config.emptyValue
		);

		/*-------------------*/

		// validate field (returns validation rule or `null`)
		let fieldErr = field.control.validate({ value, field });

		// validate rules
		const fields = field.control.validateFields({
			value,
			field,
			fields: this.data.fields,
		});

		// update the fields
		if (!isNil(fields)) {
			set(this.data, 'fields', fields);
		}

		if (get(field, 'config.readonly', false)) {
			fieldErr = null;
		}

		if (fieldErr) {
			_state.errors[name] = defaultTo(fieldErr, null);
		} else {
			_state.errors[name] = null;
		}

		/*-------------------*/

		// check if form is valid
		_state.isValid = every(_state.errors, isNil);

		const { callingfrom } = this.props;
		const { values, isValid } = _state;

		if (
			callingfrom &&
			(callingfrom === 'addressAddEdit' || callingfrom === 'contachtInform')
		) {
			Object.keys(values).forEach((key) => {
				const value = values[key];
				const transformedValue =
					key !== 'poBox' ? value.replaceAll(/[^0-9a-zA-Z]/gi, '') : value;

					
				    if (key !== 'line2' && transformedValue === '' && key !== 'poBox') {
						_state.isValid = false;
					} else if (
						key === 'poBox' &&
						transformedValue !== null &&
						transformedValue !== ''
					) {
						_state.isValid = true;
					}
			});
		}

		/*-------------------*/

		// update state
		this.setState(_state, () => {
			this.updateValuesDependentsFields({ value, field }); // calling to see if any dependent fields needs to be re-rendered

			if (get(this.props, 'submitOnChange', false)) {
				this.handleSubmit(event);
			}

			if (noop !== get(this.props, 'onChangeCallBack')) {
				this.props.onChangeCallBack({ values: this.state.values, field });
			}
			if (!updateDependent) {
				this.data.temporaryFieldValues = {}; // if all the fields updated successfully, clear the temporary state
			}
		});
	}

	/**
	 * @desc Handle for submit.
	 */
	handleSubmit(event) {
		log.debug('Form.handleSubmit()');

		// prevent default form submission behaviour
		if (event) {
			event.preventDefault();
		}

		/*-------------------*/

		// clone current form state
		const _state = cloneDeep(this.state);

		/*-------------------*/

		// validate each field and update state
		forEach(this.data.fields, (field) => {
			let fieldErr = field.control.validate({
				value: this.state.values[field.name],
				field,
			});

			if (get(field, 'config.readonly', false)) {
				fieldErr = null;
			}

			if (fieldErr) {
				_state.errors[field.name] = defaultTo(fieldErr, null);
			} else {
				_state.errors[field.name] = null;
			}
		});

		/*-------------------*/

		// check if form is valid
		_state.isValid = every(_state.errors, isNil);

		// display errors
		_state.showErrors = true;

		/*-------------------*/

		// update state and invoke callback
		this.setState(_state, () => {
			this.props.onSubmit({
				values: this.state.values,
				isValid: this.state.isValid,
			});
		});
	}

	/**
	 * @desc When location changes, update state and re-render the map
	 */
	UNSAFE_componentWillReceiveProps(newProps) {
		log.debug('Form.UNSAFE_componentWillReceiveProps()');

		// if `locations` changes, re-render map
		if (
			!isEqual(newProps.fields, this.props.fields) &&
			this.props.updatFieldsOnChange
		) {
			this.updateForm(newProps);
		}

		// Changes values on form, if container changes any conditionally
		if (get(this.props, 'updatFieldValuesOnChange', false)) {
			if (!isEqual(newProps.values, this.state.values)) {
				this.setState({
					values: newProps.values,
				});
			}
		}
	}

	updateForm(props) {
		log.debug('Form.updateForm()');

		// static data
		this.data = {
			temporaryFieldValues: {},

			// fields
			fields: mapValues(props.fields, (field) => ({
				...field,

				// is field required to submit (to display `*` character)
				required: !isEmpty(
					find(get(field, 'config.validations', []), {
						name: DATA_VALIDATIONS.REQUIRED,
					})
				),

				maxLength: get(
					find(
						get(field, 'config.validations', []),
						{ name: DATA_VALIDATIONS.MAX_LENGTH },
						{}
					),
					'maxLength',
					undefined
				),

				// form control
				control: new FormControl({
					name: field.name,
					type: field.type,
					validations: get(field, 'config.validations', []),
				}),

				// mask
				mask: get(field, 'config.mask')
					? get(MASKS, field.config.mask)
					: undefined,

				// generic field view
				view: getFieldView(field.type),
			})),
		};

		this.setState({
			showErrors: props.showErrors, // display field errors
			isValid: props.values, // is form valid
			errors: mapValues(props.fields, () => null), // field errors
			values: mapValues(props.fields, (field) =>
				defaultTo(
					{ ...this.state.values, ...props.values }[field.name],
					field.config.emptyValue
				)
			), // form values
		});
	}

	/***************************************/

	render() {
		// return form view using function as a child (FaaC)
		return this.props.children({
			fields: this.data.fields,
			values: this.state.values,
			isValid: this.state.isValid,
			errors: !this.state.showErrors ? {} : defaultTo(this.state.errors, {}), // pass errors when `showErrors` is `true`
			onChange: this.handleChange,
			onSumbit: this.handleSubmit,
		});
	}
}

// set display name
Form.displayName = 'Form';

// default props
Form.defaultProps = {
	showErrors: false, // show no errors until form submits
	fields: {}, // { <name>: config, ... }
	values: {}, // default values
	onSubmit: noop, // ( { values, isValid } ) => ...
	onChangeCallBack: noop,
	updatFieldsOnChange: false,
	updatFieldValuesOnChange: false,
};

// prop types
Form.propTypes = {};
